import React from 'react';
import {
    apiSettingsActionCreators,
    uiActionCreators,
    OrchestrationServiceInfo,
    UserSettings,
    WhiteLabelConfig,
    SessionInfo,
    ClientInfo,
    useCartAnalyticsConfig,
    AnyAction,
    ThunkActionCreator,
    LoggerHandler,
    loggerActionCreators,
    MeasurementHandler,
} from '@cart-js/core';

import { AnalyticsActions } from '@agoda/layout';
import { Feature, connectFromCrossSellWidget, translationActionCreators, featuresActionCreators, CrossSellWidgetState } from '@cart-js/trip';
import { useCartConfig } from 'core/context/WrappedCartConfig/hooks/useCartConfig';
import { useSelector, connect } from 'react-redux';
import { mapHeaderAnalyticsProps } from 'component/common/Header/header.selectors';
import { compose, createSelector } from '@reduxjs/toolkit';
import { selectPageTypeIdFromBootstrap, selectExperimentsFromBootstrap, selectFeatureExperimentsFromBootstrap } from 'store/slice/bootstrap';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';

export interface StateProps {
    shouldEnhanceUCSActivityCard: boolean;
    shouldAppendTravelDateActivityURL: boolean;
}

interface CrossSellDispatchProps {
    setClientInfo: (clientInfo: ClientInfo) => void;
    setSessionInfo: (sessionInfo: SessionInfo) => void;
    setUserSettings: (userSettings: UserSettings) => void;
    setServices: (services: OrchestrationServiceInfo) => void;
    setWhiteLabelConfig: (wlConfig: WhiteLabelConfig) => void;
    fetchCms: () => void;
    setLogger: (logger: LoggerHandler) => void;
    setMeasurement: (measurement: MeasurementHandler) => void;
    setIsWebView: (isWebView: boolean) => void;
    setTripFeature: (features: Partial<Record<Feature, boolean>>) => void;
}

type Props = StateProps & CrossSellDispatchProps;

const WrappedCrossSellConfigComponent: React.FC<Props> = (props) => {
    const pageTypeId = useSelector(selectPageTypeIdFromBootstrap);
    const { analytics } = mapHeaderAnalyticsProps(pageTypeId);
    const {
        setClientInfo,
        setUserSettings,
        setServices,
        setWhiteLabelConfig,
        setSessionInfo,
        fetchCms,
        setLogger,
        setMeasurement,
        setIsWebView,
        setTripFeature,
        shouldEnhanceUCSActivityCard,
        shouldAppendTravelDateActivityURL,
    } = props;

    useCartConfig({
        setClientInfo,
        setUserSettings,
        setServices,
        setWhiteLabelConfig,
        setSessionInfo,
        setLogger,
        setMeasurement,
        setIsWebView,
    });

    React.useEffect(() => {
        fetchCms();
    }, [fetchCms]);

    useCartAnalyticsConfig({
        visit: analytics.visit,
        leave: analytics.leave,
        click: (elementName: string, additionalProps?: object) => analytics.click(elementName, 2, additionalProps),
        seen: (elementName: string, additionalProps?: object) =>
            analytics.custom(AnalyticsActions.Seen, {
                ...additionalProps,
                action_element_name: elementName,
                event_version: 2,
            }),
    });

    React.useEffect(() => {
        const features: Partial<Record<Feature, boolean>> = {
            [Feature.EnhanceUCSActivityCard]: shouldEnhanceUCSActivityCard,
            [Feature.AppendTravelDateActivityURL]: shouldAppendTravelDateActivityURL,
        };
        setTripFeature(features);
    }, [setTripFeature, shouldEnhanceUCSActivityCard, shouldAppendTravelDateActivityURL]);

    return null;
};

WrappedCrossSellConfigComponent.displayName = 'WrappedCartConfigComponent';

type ThunkDispatch<S> = React.Dispatch<AnyAction | ThunkActionCreator<S, Promise<void>>>;

const mapStateToProps = createSelector(selectExperimentsFromBootstrap, selectFeatureExperimentsFromBootstrap, (experiments, featureExperiment): StateProps => {
    return {
        shouldEnhanceUCSActivityCard: isB(ExperimentId.ENHANCE_UCS_ACTIVITY_CARD, experiments),
        shouldAppendTravelDateActivityURL: featureExperiment.featureBehavior.AppendTravelDateActivityUrl,
    };
});

const mapCrossSellDispatchToProps = (dispatch: ThunkDispatch<CrossSellWidgetState>): CrossSellDispatchProps => ({
    setClientInfo: (clientInfo: ClientInfo) => dispatch(apiSettingsActionCreators.setClientInfo(clientInfo)),
    setUserSettings: (userSettings: UserSettings) => dispatch(apiSettingsActionCreators.setUserSettings(userSettings)),
    setServices: (services: OrchestrationServiceInfo) => dispatch(apiSettingsActionCreators.addService(services)),
    setWhiteLabelConfig: (wlConfig: WhiteLabelConfig) => dispatch(apiSettingsActionCreators.setWhiteLabelConfig(wlConfig)),
    setSessionInfo: (sessionInfo: SessionInfo) => dispatch(apiSettingsActionCreators.setSessionInfo(sessionInfo)),
    fetchCms: () => dispatch(translationActionCreators.fetchCms()),
    setLogger: (logger: LoggerHandler) => dispatch(loggerActionCreators.setLoggerHandler(logger) as unknown as AnyAction),
    setMeasurement: (measurement: MeasurementHandler) => dispatch(loggerActionCreators.setMeasurementHandler(measurement) as unknown as AnyAction),
    setIsWebView: (isWebView: boolean) => dispatch(uiActionCreators.setIsWebView(isWebView)),
    setTripFeature: (features: Partial<Record<Feature, boolean>>) => dispatch(featuresActionCreators.updateFeatures(features)),
});

const WrappedCrossSellConfig = compose<React.ComponentType>(
    connect(mapStateToProps),
    connectFromCrossSellWidget(() => ({}), mapCrossSellDispatchToProps),
)(WrappedCrossSellConfigComponent);

export { WrappedCrossSellConfig, WrappedCrossSellConfigComponent, mapStateToProps, mapCrossSellDispatchToProps };
